import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import ApiService from '../../services/api';

interface Package {
  id: number;
  name: string;
  description: string;
  originalPrice: number;
  discountedPrice: number;
  services: string[];
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface Service {
  id: number;
  name: string;
  price: number;
}

const PackageManagement: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    originalPrice: '',
    discountedPrice: '',
    services: [] as string[],
    displayOrder: ''
  });

  // Paketleri yükle
  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminPackages();
      setPackages(response.packages);
    } catch (error: any) {
      console.error('Paketler yüklenirken hata:', error);
      setError('Paketler yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Hizmetleri yükle
  const loadServices = async () => {
    try {
      const response = await ApiService.getServices();
      setServices(response.services);
    } catch (error: any) {
      console.error('Hizmetler yüklenirken hata:', error);
    }
  };

  useEffect(() => {
    loadPackages();
    loadServices();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  // Modal aç
  const openModal = (pkg: Package | null = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description,
        originalPrice: pkg.originalPrice.toString(),
        discountedPrice: pkg.discountedPrice.toString(),
        services: pkg.services,
        displayOrder: pkg.displayOrder.toString()
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        originalPrice: '',
        discountedPrice: '',
        services: [],
        displayOrder: ''
      });
    }
    setShowModal(true);
  };

  // Modal kapat
  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
    setError(null);
  };

  // Form gönder
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const packageData = {
        name: formData.name,
        description: formData.description,
        originalPrice: parseFloat(formData.originalPrice),
        discountedPrice: parseFloat(formData.discountedPrice),
        services: formData.services,
        displayOrder: parseInt(formData.displayOrder) || 0
      };

      if (editingPackage) {
        await ApiService.updatePackage(editingPackage.id, packageData);
      } else {
        await ApiService.createPackage(packageData);
      }

      await loadPackages();
      closeModal();
    } catch (error: any) {
      console.error('Paket kaydedilirken hata:', error);
      setError('Paket kaydedilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Paket sil
  const deletePackage = async (id: number) => {
    if (!window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deletePackage(id);
      await loadPackages();
    } catch (error: any) {
      console.error('Paket silinirken hata:', error);
      setError('Paket silinemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Paket durumunu değiştir
  const togglePackageStatus = async (id: number) => {
    try {
      setLoading(true);
      await ApiService.togglePackageStatus(id);
      await loadPackages();
    } catch (error: any) {
      console.error('Paket durumu değiştirilirken hata:', error);
      setError('Paket durumu değiştirilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Hizmet seçimi
  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  return (
    <div className="p-6 text-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Paket Yönetimi</h2>
        <button
          onClick={() => openModal()}
          className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 flex items-center gap-2"
          disabled={loading}
        >
          <Plus size={20} />
          Yeni Paket
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        </div>
      )}

              <div className="rounded-lg shadow overflow-hidden border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                  <table className="min-w-full divide-y divide-gray-700">
                      <thead style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Paket Adı
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Açıklama
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fiyat
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hizmetler
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Durum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
                      <tbody className="divide-y divide-gray-700" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            {packages.map((pkg) => (
                              <tr key={pkg.id} className="hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">{pkg.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-white max-w-xs truncate">{pkg.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-white">
                    <span className="line-through text-gray-500">{pkg.originalPrice}₺</span>
                    <span className="ml-2 font-bold text-pink-600">{pkg.discountedPrice}₺</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-white">
                    {pkg.services.length} hizmet
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    pkg.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {pkg.isActive ? 'Aktif' : 'Pasif'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => togglePackageStatus(pkg.id)}
                      className={`${pkg.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                      disabled={loading}
                    >
                      {pkg.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => openModal(pkg)}
                      className="text-indigo-600 hover:text-indigo-900"
                      disabled={loading}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => deletePackage(pkg.id)}
                      className="text-red-600 hover:text-red-900"
                      disabled={loading}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-white mb-4">
                {editingPackage ? 'Paket Düzenle' : 'Yeni Paket Ekle'}
              </h3>
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Paket Adı
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Orijinal Fiyat (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.originalPrice}
                      onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      İndirimli Fiyat (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discountedPrice}
                      onChange={(e) => setFormData({...formData, discountedPrice: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                      style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({...formData, displayOrder: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Dahil Edilen Hizmetler
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
                    {services.map((service) => (
                      <label key={service.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service.id.toString())}
                          onChange={() => handleServiceToggle(service.id.toString())}
                          className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                        />
                        <span className="text-sm text-white">{service.name} - {service.price}₺</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={loading}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Kaydediliyor...' : (editingPackage ? 'Güncelle' : 'Ekle')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageManagement;
